import React, {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type HeaderTab = {
  icon: ReactNode;
  title: string;
  to: string;
};

export interface HeaderContent {
  title: string;
  tabs: HeaderTab[];
}

export type Header = HeaderContent & {
  setTitle: (value: string) => void;
  setTabs: (value: HeaderTab[]) => void;
  setHeader: Dispatch<SetStateAction<HeaderContent>>;
};

export const HeaderServiceContext = createContext<Header>({
  title: '',
  tabs: [],
  setTitle: () => {},
  setTabs: () => {},
  setHeader: () => {},
});

const HeaderServiceProvider = ({ children }: PropsWithChildren) => {
  const [header, setHeader] = useState<HeaderContent>({
    title: '',
    tabs: [],
  });

  const contextPayload = useMemo<Header>(
    () => ({
      ...header,
      setHeader,
      setTitle: (value: string) => {
        try {
          setHeader({ ...header, title: value });
        } catch (e) {
          console.warn(e);
        }
      },
      setTabs: (value: HeaderTab[]) => {
        try {
          setHeader({ ...header, tabs: value });
        } catch (e) {
          console.warn(e);
        }
      },
    }),
    [header, setHeader]
  );

  return (
    <HeaderServiceContext.Provider value={contextPayload}>
      {children}
    </HeaderServiceContext.Provider>
  );
};

export const useHeader = (title?: string, tabs?: HeaderTab[]) => {
  const header = useContext(HeaderServiceContext);
  useEffect(() => {
    header.setHeader((value) => ({
      title: title && header.title !== title ? title : value.title,
      tabs: tabs || value.tabs,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return header;
};

export default HeaderServiceProvider;
